import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { NgxMatDatetimePicker } from '@angular-material-components/datetime-picker';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Request, RequestSubtask } from '../../../../models/request.model';
import { RequestsV2ImplService } from '../../../../services/requests-v2.impl.service';
import { CreateRequestSubtaskDto } from '../../../../models/request-subtask.model';
import { AdminUser, SubtaskAssigneeMenuComponent } from '../../../../components/shared/subtask-assignee-menu/subtask-assignee-menu.component';
import { UserService } from 'src/app/user/service/user.abstract.service';
import { parseDateForDisplay } from 'src/app/global/utils/date/date.utils';

export interface AssigneeUser {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    picture: string;
}

@Component({
    selector: 'app-request-subtasks',
    templateUrl: './request-subtasks.component.html',
    styleUrls: ['./request-subtasks.component.scss'],
})
export class RequestSubtasksComponent implements OnInit, OnDestroy, OnChanges {
    @Input() request: Request;
    @Input() canEdit = false;
    @Output() subtasksUpdated = new EventEmitter<void>();
    @ViewChild('assigneeMenu') assigneeMenu: SubtaskAssigneeMenuComponent;
    @ViewChild('newTaskInput') newTaskInput: ElementRef;
    @ViewChild('sharedCompletedAtPicker') sharedCompletedAtPicker: NgxMatDatetimePicker<Date>;
    @ViewChild('sharedDuePicker') sharedDuePicker: MatDatepicker<any>;

    subtasks: RequestSubtask[] = [];
    newSubtaskDescription = '';
    newDueDate = '';

    private _isAdding = false;
    get isAdding(): boolean {
        return this._isAdding;
    }
    set isAdding(value: boolean) {
        this._isAdding = value;
        if (value) {
            setTimeout(() => this.newTaskInput?.nativeElement?.focus(), 50);
        }
    }

    isCreating = false;
    confirmingDeleteId: string | null = null;
    editingTaskId: string | null = null;
    editingTaskText = '';
    private isSavingTaskText = false;

    activeSubtaskId: string | null = null;
    assigningSubtaskId: string | null = null;
    editingCompletedAtSubtaskId: string | null = null;
    updatingCompletedAtSubtaskId: string | null = null;
    editingDueDateSubtaskId: string | null = null;
    private _completedAtSaved = false;
    currentLocale: string;
    preloadedAdminData: AdminUser[] = [];
    selectedAssignee: AdminUser | null = null;

    private destroy$ = new Subject<void>();

    constructor(
        private requestsService: RequestsV2ImplService,
        private translate: TranslateService,
        private elementRef: ElementRef,
        private userService: UserService,
    ) {}

    ngOnInit(): void {
        this.currentLocale = this.translate.currentLang === 'fr' ? 'fr-CA' : 'en-CA';
        this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe((event) => {
            this.currentLocale = event.lang === 'fr' ? 'fr-CA' : 'en-CA';
        });
        this.syncSubtasksFromRequest();
        this.loadAdminUsers();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.request && !changes.request.firstChange) {
            this.syncSubtasksFromRequest();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    @HostListener('document:mousedown', ['$event'])
    onDocumentMousedown(event: MouseEvent): void {
        if (!this.elementRef.nativeElement.contains(event.target as Node)) {
            if (this.confirmingDeleteId) this.confirmingDeleteId = null;
            if (this.isAdding && !this.newSubtaskDescription.trim()) this.dismissAdding();
        }
    }

    private syncSubtasksFromRequest(): void {
        if (!this.request?.subtasks) {
            this.subtasks = [];
            return;
        }
        this.subtasks = this.request.subtasks.map((subtask) => ({
            _id: subtask._id,
            description: subtask.description,
            isDone: subtask.isDone,
            completedAt: subtask.completedAt,
            dueDate: subtask.dueDate,
            assignedTo: subtask.assignedTo
                ? {
                      _id: subtask.assignedTo._id,
                      firstName: subtask.assignedTo.firstName || '',
                      lastName: subtask.assignedTo.lastName || '',
                      email: subtask.assignedTo.email || '',
                      picture: subtask.assignedTo.picture || '',
                  }
                : null,
        }));
    }

    loadAdminUsers(): void {
        this.userService
            .searchAdmins('', 20)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (admins) => {
                    this.preloadedAdminData = admins.map((admin) => ({
                        id: admin.id || admin._id,
                        name: admin.name || `${admin.firstName} ${admin.lastName}`,
                        email: admin.email || '',
                        picture: admin.picture || '',
                        initials: this.getInitials(admin.name || `${admin.firstName} ${admin.lastName}`),
                    }));
                },
                error: () => {
                    this.preloadedAdminData = [];
                },
            });
    }

    toggleSubtask(subtaskId: string): void {
        const subtask = this.subtasks.find((st) => st._id === subtaskId);
        if (!subtask) return;

        subtask.isDone = !subtask.isDone;
        const completedAt = subtask.isDone ? new Date().toISOString() : undefined;

        this.requestsService
            .setRequestSubtaskCompletion(this.request._id, subtaskId, subtask.isDone, completedAt)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (updatedSubtask) => {
                    const index = this.subtasks.findIndex((st) => st._id === subtaskId);
                    if (index !== -1) {
                        this.subtasks[index] = { ...this.subtasks[index], isDone: updatedSubtask.isDone };
                    }
                    this.subtasksUpdated.emit();
                },
                error: () => {
                    if (subtask) subtask.isDone = !subtask.isDone;
                },
            });
    }

    createSubtask(): void {
        const description = this.newSubtaskDescription.trim();
        if (!description || this.isCreating) return;

        this.isCreating = true;
        const payload: CreateRequestSubtaskDto = {
            description,
            isDone: false,
            assignedTo: this.selectedAssignee ? this.selectedAssignee.id : undefined,
            dueDate: this.newDueDate || undefined,
        };

        this.requestsService
            .updateRequestSubtasksList(this.request._id, [payload])
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.newSubtaskDescription = '';
                    this.selectedAssignee = null;
                    this.newDueDate = '';
                    this.isAdding = false;
                    this.isCreating = false;
                    this.subtasksUpdated.emit();
                },
                error: () => {
                    this.isCreating = false;
                },
            });
    }

    startTextEdit(subtask: RequestSubtask): void {
        if (this.confirmingDeleteId) return;
        this.editingTaskId = subtask._id;
        this.editingTaskText = subtask.description;
        setTimeout(() => {
            const input = this.elementRef.nativeElement.querySelector('.task-text-input') as HTMLInputElement;
            if (input) {
                input.focus();
                input.select();
            }
        }, 0);
    }

    saveTextEdit(subtask: RequestSubtask): void {
        if (this.editingTaskId !== subtask._id || this.isSavingTaskText) return;
        const newText = this.editingTaskText.trim();
        if (!newText || newText === subtask.description) {
            this.cancelTextEdit();
            return;
        }
        this.isSavingTaskText = true;
        this.editingTaskId = null;
        this.requestsService
            .updateRequestSubtask(this.request._id, subtask._id, { description: newText })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    const index = this.subtasks.findIndex((st) => st._id === subtask._id);
                    if (index !== -1) this.subtasks[index] = { ...this.subtasks[index], description: newText };
                    this.isSavingTaskText = false;
                    this.subtasksUpdated.emit();
                },
                error: () => {
                    this.isSavingTaskText = false;
                },
            });
    }

    cancelTextEdit(): void {
        this.editingTaskId = null;
        this.editingTaskText = '';
    }

    dismissAdding(): void {
        this.isAdding = false;
        this.newSubtaskDescription = '';
        this.selectedAssignee = null;
        this.newDueDate = '';
    }

    executeDelete(subtaskId: string): void {
        this.requestsService
            .deleteRequestSubtask(this.request._id, subtaskId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.subtasks = this.subtasks.filter((st) => st._id !== subtaskId);
                    this.confirmingDeleteId = null;
                    this.subtasksUpdated.emit();
                },
                error: () => {
                    this.confirmingDeleteId = null;
                },
            });
    }

    prepareCompletedAtEdit(subtask: RequestSubtask): void {
        this.editingCompletedAtSubtaskId = subtask._id;
        this._completedAtSaved = false;
        setTimeout(() => this.sharedCompletedAtPicker?.open());
    }

    onCompletedAtDateChange(event: any): void {
        this._completedAtSaved = true;
        const subtask = this.subtasks.find((st) => st._id === this.editingCompletedAtSubtaskId);
        if (!subtask) return;
        const raw = event.value;
        if (!raw) return;
        const selected: Date = raw.toDate ? raw.toDate() : new Date(raw);
        if (isNaN(selected.getTime())) return;

        this.updatingCompletedAtSubtaskId = subtask._id;
        this.requestsService
            .setRequestSubtaskCompletion(this.request._id, subtask._id, true, selected.toISOString())
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.updatingCompletedAtSubtaskId = null;
                    this.editingCompletedAtSubtaskId = null;
                    this.subtasksUpdated.emit();
                },
                error: () => {
                    this.updatingCompletedAtSubtaskId = null;
                    this.editingCompletedAtSubtaskId = null;
                },
            });
    }

    onPickerClosed(): void {
        if (!this._completedAtSaved) {
            this.editingCompletedAtSubtaskId = null;
        }
        this._completedAtSaved = false;
    }

    openAssigneeMenu(event: Event, subtaskId: string, subtask?: any): void {
        event.stopPropagation();
        this.activeSubtaskId = subtaskId;
        const target = event.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        const position = {
            top: rect.bottom + window.scrollY + 5,
            left: rect.left + window.scrollX - 230,
        };
        let assigneeId = null;
        if (subtaskId === 'new') {
            assigneeId = this.selectedAssignee ? this.selectedAssignee.id : null;
        } else {
            const found = this.subtasks.find((st) => st._id === subtaskId);
            assigneeId = found?.assignedTo ? found.assignedTo._id : null;
        }
        this.assigneeMenu.open(position, assigneeId, this.preloadedAdminData);
    }

    onAssigneeSelected(assignee: AdminUser | null): void {
        this.handleAssigneeSelected(assignee);
    }

    private handleAssigneeSelected(assignee: AdminUser | null): void {
        if (!this.activeSubtaskId) return;

        if (this.activeSubtaskId === 'new') {
            this.selectedAssignee = assignee;
            return;
        }

        const subtaskId = this.activeSubtaskId;
        const subtask = this.subtasks.find((st) => st._id === subtaskId);
        if (subtask) {
            subtask.assignedTo = assignee
                ? {
                      _id: assignee.id,
                      firstName: assignee.name.split(' ')[0] || '',
                      lastName: assignee.name.split(' ').slice(1).join(' ') || '',
                      email: assignee.email || '',
                      picture: assignee.picture || '',
                  }
                : null;
        }

        this.assigningSubtaskId = subtaskId;

        this.requestsService
            .assignRequestSubtask(this.request._id, subtaskId, assignee ? assignee.id : null)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (updatedSubtask) => {
                    this.assigningSubtaskId = null;
                    this.activeSubtaskId = null;
                    const index = this.subtasks.findIndex((st) => st._id === subtaskId);
                    if (index !== -1) {
                        if (updatedSubtask.assignedTo && typeof updatedSubtask.assignedTo === 'object') {
                            this.subtasks[index] = { ...updatedSubtask, assignedTo: updatedSubtask.assignedTo };
                        } else if (assignee) {
                            this.subtasks[index] = {
                                ...updatedSubtask,
                                assignedTo: {
                                    _id: assignee.id,
                                    firstName: assignee.name.split(' ')[0] || '',
                                    lastName: assignee.name.split(' ').slice(1).join(' ') || '',
                                    email: assignee.email,
                                    picture: assignee.picture || '',
                                },
                            };
                        } else {
                            this.subtasks[index] = { ...updatedSubtask, assignedTo: null };
                        }
                    }
                    this.subtasksUpdated.emit();
                },
                error: () => {
                    this.assigningSubtaskId = null;
                    if (subtask) {
                        const originalSubtask = this.request.subtasks?.find((st) => st._id === subtaskId);
                        subtask.assignedTo = originalSubtask?.assignedTo || null;
                    }
                },
            });
    }

    clearDueDate(): void {
        const subtaskId = this.editingDueDateSubtaskId;
        this.editingDueDateSubtaskId = null;

        if (subtaskId === 'new') {
            this.newDueDate = '';
            return;
        }

        if (!subtaskId) return;

        const subtask = this.subtasks.find((st) => st._id === subtaskId);
        if (!subtask?.dueDate) return;

        this.requestsService
            .updateRequestSubtask(this.request._id, subtask._id, { dueDate: null })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    const index = this.subtasks.findIndex((st) => st._id === subtask._id);
                    if (index !== -1) this.subtasks[index] = { ...this.subtasks[index], dueDate: null };
                    this.subtasksUpdated.emit();
                },
            });
    }

    openDueDatePicker(subtaskId: string): void {
        this.editingDueDateSubtaskId = subtaskId;
        setTimeout(() => this.sharedDuePicker?.open());
    }

    onDueDateChange(event: any): void {
        const subtaskId = this.editingDueDateSubtaskId;
        this.editingDueDateSubtaskId = null;

        const raw = event.value;
        if (!raw) return;
        const date: Date = raw.toDate ? raw.toDate() : new Date(raw);
        if (isNaN(date.getTime())) return;
        const isoDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

        if (subtaskId === 'new') {
            this.newDueDate = isoDate;
            return;
        }

        const subtask = this.subtasks.find((st) => st._id === subtaskId);
        if (!subtask) return;

        this.requestsService
            .updateRequestSubtask(this.request._id, subtask._id, { dueDate: isoDate })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    const index = this.subtasks.findIndex((st) => st._id === subtask._id);
                    if (index !== -1) this.subtasks[index] = { ...this.subtasks[index], dueDate: isoDate };
                    this.subtasksUpdated.emit();
                },
            });
    }

    formatDueDate(iso: string): string {
        const d = parseDateForDisplay(iso);
        if (!d) return '';
        const locale = this.translate.currentLang === 'fr' ? 'fr-CA' : 'en-CA';
        return d.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
    }

    getDueStatus(iso: string): 'past' | 'soon' | 'future' {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const due = parseDateForDisplay(iso);
        if (!due) return 'future';
        due.setHours(0, 0, 0, 0);
        const diffDays = Math.round((due.getTime() - today.getTime()) / 86400000);
        if (diffDays < 0) return 'past';
        if (diffDays <= 1) return 'soon';
        return 'future';
    }

    getAssigneeInitials(assignee: AssigneeUser): string {
        if (!assignee) return '';
        const firstInitial = assignee.firstName ? assignee.firstName.charAt(0) : '';
        const lastInitial = assignee.lastName ? assignee.lastName.charAt(0) : '';
        return (firstInitial + lastInitial).toUpperCase();
    }

    getInitials(name: string): string {
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
    }
}
